/**
 * World-map registry — parses + validates `worldmap.json` once at load and
 * exposes the atlas graph plus helpers over it (node lookup, the unlock chain).
 * Single source of truth for both `WorldMapScene` (rendering) and `Progress`
 * (which nodes a clear unlocks).
 */
import worldMapJson from './worldmap.json';
import { parseWorldMap, type MapNode, type Region } from './schema/worldmap';

export const WORLD_MAP = parseWorldMap(worldMapJson);

const NODE_BY_ID = new Map<string, MapNode>(WORLD_MAP.nodes.map((n) => [n.id, n]));
const REGION_BY_ID = new Map<string, Region>(WORLD_MAP.regions.map((r) => [r.id, r]));

export function mapNode(id: string): MapNode | undefined {
  return NODE_BY_ID.get(id);
}

export function mapRegion(id: string): Region | undefined {
  return REGION_BY_ID.get(id);
}

/** Nodes unlocked from the very start (chain roots — no prerequisite). */
export function rootNodeIds(): string[] {
  return WORLD_MAP.nodes.filter((n) => n.prereq === null).map((n) => n.id);
}

/** Node ids whose prerequisite is `nodeId` — i.e. the stops that open once
 *  `nodeId` is cleared. (Linear today, but modelled as a graph for branching.) */
export function nodesUnlockedBy(nodeId: string): string[] {
  return WORLD_MAP.nodes.filter((n) => n.prereq === nodeId).map((n) => n.id);
}

/** The node that launches a given level id (the inverse of node → levelId). */
export function nodeForLevel(levelId: string): MapNode | undefined {
  return WORLD_MAP.nodes.find((n) => n.levelId === levelId);
}
